import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import AppLayout from "../components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  MessageSquare, Search, Download, Globe, User, Clock, ExternalLink, AlertTriangle, Flag,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatLogPage() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [active, setActive] = useState(null); // {session, messages}
  const [activeLoading, setActiveLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/chat/sessions", { params });
      setSessions(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openSession = async (id) => {
    setActiveLoading(true);
    setActive(null);
    try {
      const { data } = await api.get(`/chat/sessions/${id}`);
      setActive(data);
    } finally {
      setActiveLoading(false);
    }
  };

  const exportCsv = () => {
    const url = `${API}/chat/export.csv`;
    window.open(url, "_blank");
  };

  const total = sessions.length;
  const oosCount = useMemo(
    () => sessions.filter((s) => (s.last_preview || "").toLowerCase().includes("kan dessverre ikke")).length,
    [sessions],
  );

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto p-4 lg:p-8 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#0F1A2B] flex items-center gap-2">
              <MessageSquare className="h-6 w-6 text-[#A57F39]" /> Chat-logg
            </h1>
            <p className="text-sm text-[#2B3F4D]/70 mt-1">
              {total} samtaler · {oosCount} markert som utenfor Canal Boats-tema
            </p>
          </div>
          <Button
            onClick={exportCsv}
            data-testid="chat-log-export"
            className="bg-[#A57F39] hover:bg-[#BD984D] text-white"
          >
            <Download className="h-4 w-4 mr-2" /> Eksporter CSV
          </Button>
        </div>

        <Card className="border-[#A57F39]/20">
          <CardContent className="p-4 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <label className="text-xs uppercase tracking-widest text-[#0F1A2B]/60 mb-1 block">
                Søk (navn, e-post, melding)
              </label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#A57F39]" />
                <Input
                  data-testid="chat-log-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="F.eks. pris, Greenline, kari@…"
                  className="pl-9"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#0F1A2B]/60 mb-1 block">Fra</label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="chat-log-from" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#0F1A2B]/60 mb-1 block">Til</label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="chat-log-to" />
            </div>
            <Button onClick={load} className="bg-[#0F1A2B] hover:bg-[#2B3F4D] text-white" data-testid="chat-log-apply">
              Søk
            </Button>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-1 space-y-2 max-h-[70vh] overflow-y-auto pr-1">
            {loading && <div className="text-sm text-[#2B3F4D]/60">Laster…</div>}
            {!loading && sessions.length === 0 && (
              <div className="text-sm text-[#2B3F4D]/60 py-8 text-center">Ingen samtaler ennå.</div>
            )}
            {sessions.map((s) => (
              <button
                key={s.id}
                type="button"
                data-testid={`chat-log-session-${s.id}`}
                onClick={() => openSession(s.id)}
                className={`w-full text-left rounded-xl border p-3 bg-white transition hover:shadow-sm ${
                  active?.session?.id === s.id
                    ? "border-[#A57F39] ring-1 ring-[#A57F39]/40"
                    : "border-[#A57F39]/15 hover:border-[#A57F39]/40"
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="text-sm font-semibold text-[#0F1A2B] truncate">
                    {s.user_name || "Anonym besøkende"}
                  </div>
                  <Badge className="bg-[#A57F39]/10 text-[#A57F39] border border-[#A57F39]/20 uppercase text-[9px]">
                    {s.last_lang || s.lang || "no"}
                  </Badge>
                </div>
                <div className="text-xs text-[#2B3F4D]/70 truncate mb-1">
                  {s.user_email || s.page || "—"}
                </div>
                <div className="text-xs text-[#0F1A2B]/80 line-clamp-2">
                  {s.last_preview || "—"}
                </div>
                <div className="flex items-center justify-between mt-1 text-[10px] text-[#2B3F4D]/60">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {new Date(s.last_message_at || s.created_at).toLocaleString("no-NO")}
                  </span>
                  <span>{s.message_count || 0} msg</span>
                </div>
              </button>
            ))}
          </div>

          <Card className="lg:col-span-2 border-[#A57F39]/20 min-h-[400px]">
            <CardHeader>
              <CardTitle className="text-base text-[#0F1A2B] flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-[#A57F39]" />
                {active ? (active.session.user_name || "Anonym besøkende") : "Velg en samtale"}
              </CardTitle>
              {active && (
                <div className="flex flex-wrap gap-2 text-xs text-[#2B3F4D]/70">
                  <span className="flex items-center gap-1"><User className="h-3 w-3" /> {active.session.user_email || "ingen e-post"}</span>
                  <span className="flex items-center gap-1"><Globe className="h-3 w-3" /> {active.session.last_lang || active.session.lang}</span>
                  {active.session.page && (
                    <span className="flex items-center gap-1"><ExternalLink className="h-3 w-3" /> {active.session.page}</span>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-2 max-h-[60vh] overflow-y-auto">
              {activeLoading && <div className="text-sm text-[#2B3F4D]/60">Henter…</div>}
              {!activeLoading && !active && (
                <div className="text-sm text-[#2B3F4D]/60 py-10 text-center">Åpne en samtale fra lista til venstre for å lese den.</div>
              )}
              {active?.messages?.map((m) => (
                <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#A57F39] text-white rounded-br-sm"
                      : "bg-[#F8F7F2] text-[#0F1A2B] border border-[#A57F39]/15 rounded-bl-sm"
                  }`}>
                    {m.content}
                    {m.out_of_scope && (
                      <div className="mt-1 flex items-center gap-1 text-[10px] uppercase tracking-widest text-amber-700">
                        <AlertTriangle className="h-3 w-3" /> Utenfor tema
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-2 mt-1">
                      <div className="text-[9px] uppercase tracking-widest opacity-60">
                        {new Date(m.created_at).toLocaleString("no-NO")}
                      </div>
                      {m.role === "assistant" && (
                        <a
                          href={`/admin/volt-training?title=${encodeURIComponent("Korreksjon fra chat-logg")}&instruction=${encodeURIComponent("Rett opp: Volt svarte feil – «" + (m.content || "").slice(0, 180) + "». Riktig svar skal være: ")}&source_message_id=${m.id}&source_session_id=${active.session.id}`}
                          data-testid={`chat-log-flag-${m.id}`}
                          className="text-[10px] uppercase tracking-widest font-semibold text-[#A57F39] hover:text-[#BD984D] inline-flex items-center gap-1"
                        >
                          <Flag className="h-3 w-3" /> Marker som feil
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
